import {
  addDoc,
  arrayRemove,
  arrayUnion,
  deleteField,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import {
  attendeesCollectionRef,
  checkInDatesCollectionRef,
  db,
} from '../firebase';
import {
  AuditActor,
  asFirestoreData,
  logActivity,
  stripSentinels,
} from '../audit-log';
import { GROUP_PREFERENCE_NOTE_MAX_LENGTH } from '../constants';
import { diffFields } from '../diff-fields';
import type { Attendee, CheckinDate } from '../types';

const attendeeLabel = (a: { first_name: string; last_name: string }): string =>
  `${a.first_name} ${a.last_name}`.trim();

export const createAttendee = async (
  actor: AuditActor | null,
  attendeePayload: Record<string, unknown>,
): Promise<string> => {
  const docRef = await addDoc(attendeesCollectionRef, attendeePayload);
  const snapshot = stripSentinels(attendeePayload);
  logActivity({
    actor,
    entity: 'attendee',
    entityRef: docRef.id,
    entityLabel: attendeeLabel(
      snapshot as { first_name: string; last_name: string },
    ),
    action: 'create',
    eventRef: snapshot.eventRef as string | undefined,
    snapshot,
  });
  return docRef.id;
};

// Every attendee edit invalidates the cached label URL: name, group,
// grade, color badge — anything the printed badge can show might have
// changed, and we'd rather pay one extra `createLabelPdf` call on the
// next print than ship a stale label. `s3LabelUrl` is in the
// `diffFields` exclude list so this doesn't add audit-log noise.
//
// v2 (2026-05-31): `group_preference_fulfilled` is NOT cleared on
// `groupRef` changes anymore. Status is an admin assertion about the
// preference, not the placement; admin reconciles if a move made it
// stale. See docs/superpowers/specs/2026-05-27-group-preference-fulfillment-design.md.
export const updateAttendee = async (
  actor: AuditActor | null,
  before: Attendee,
  payload: Record<string, unknown>,
): Promise<void> => {
  const fullPayload = {
    ...payload,
    s3LabelUrl: deleteField(),
  };
  await updateDoc(
    doc(attendeesCollectionRef, before.ref),
    asFirestoreData(fullPayload),
  );
  const changes = diffFields(before, fullPayload);
  if (Object.keys(changes).length === 0) return;
  logActivity({
    actor,
    entity: 'attendee',
    entityRef: before.ref,
    entityLabel: attendeeLabel(before),
    action: 'update',
    eventRef: before.eventRef,
    changes,
  });
};

// Admin toggle for the Roster's "preference fulfilled" icon. Accepts
// any non-`'auto'` state (auto is only written by autoAssignAttendees).
// Routes through `updateAttendee` so the change is audited and label
// cache invalidation stays consistent across paths (minor `s3LabelUrl`
// re-fetch cost is accepted to keep this audit-log-aware).
export const setAttendeeGroupPreferenceFulfilled = async (
  actor: AuditActor | null,
  attendee: Attendee,
  fulfilled: 'manual' | 'rejected' | null,
): Promise<void> => {
  await updateAttendee(actor, attendee, {
    group_preference_fulfilled: fulfilled ?? deleteField(),
  });
};

// Admin-edited free-text note attached to an attendee's group_preference.
// Visible to all enabled users in the Roster tooltip; written only by
// admins via the pencil-icon popover. Trims, normalizes empty → null,
// clamps over-length to the cap, and routes through `updateAttendee`
// for audit-log + diff semantics. Same `s3LabelUrl` invalidation
// side-effect as every other updateAttendee call; harmless (note text
// isn't on the printed badge).
export const setAttendeeGroupPreferenceNote = async (
  actor: AuditActor | null,
  attendee: Attendee,
  note: string | null,
): Promise<void> => {
  const trimmed = note?.trim() ?? '';
  const clamped =
    trimmed.length > GROUP_PREFERENCE_NOTE_MAX_LENGTH
      ? trimmed.slice(0, GROUP_PREFERENCE_NOTE_MAX_LENGTH)
      : trimmed;
  await updateAttendee(actor, attendee, {
    group_preference_note: clamped || deleteField(),
  });
};

// Soft-deletes an attendee and, when `checkInDates` is provided, also
// prunes the attendee's ref from each date's `checkedInRefs` /
// `checkedOutRefs` arrays in the same batched commit. Cleaning the arrays
// here keeps the "X checked in / Y checked out" counters honest after
// a delete — otherwise the count would still include the now-hidden row
// (the UI defensively filters those out, but we'd rather not store the
// stale refs to begin with).
export const softDeleteAttendee = async (
  actor: AuditActor | null,
  attendee: Attendee,
  checkInDates: CheckinDate[] = [],
): Promise<void> => {
  const relevantDates = checkInDates.filter(
    (d) =>
      d.eventRef === attendee.eventRef &&
      ((d.checkedInRefs ?? []).includes(attendee.ref) ||
        (d.checkedOutRefs ?? []).includes(attendee.ref)),
  );

  if (relevantDates.length === 0) {
    await updateDoc(doc(attendeesCollectionRef, attendee.ref), {
      is_deleted: true,
    });
  } else {
    const batch = writeBatch(db);
    batch.update(doc(attendeesCollectionRef, attendee.ref), {
      is_deleted: true,
    });
    relevantDates.forEach((d) => {
      batch.update(doc(checkInDatesCollectionRef, d.ref), {
        checkedInRefs: arrayRemove(attendee.ref),
        checkedOutRefs: arrayRemove(attendee.ref),
      });
    });
    await batch.commit();
  }

  const { ref, ...rest } = attendee;
  logActivity({
    actor,
    entity: 'attendee',
    entityRef: attendee.ref,
    entityLabel: attendeeLabel(attendee),
    action: 'delete',
    eventRef: attendee.eventRef,
    snapshot: rest as unknown as Record<string, unknown>,
    ...(relevantDates.length > 0
      ? { meta: { prunedFromCheckInDates: relevantDates.length } }
      : {}),
  });
};

// Check-in / check-out toggle the matching `checkedInRefs` / `checkedOutRefs`
// array on the active CheckinDate doc. `mode` carries both directions so the
// caller can distinguish a real check-in from an "undo".
export const checkInAttendee = async (
  actor: AuditActor | null,
  date: CheckinDate,
  attendee: Pick<Attendee, 'ref' | 'first_name' | 'last_name' | 'eventRef'>,
  mode: 'check-in' | 'uncheck-in',
): Promise<void> => {
  await updateDoc(doc(checkInDatesCollectionRef, date.ref), {
    checkedInRefs:
      mode === 'check-in' ? arrayUnion(attendee.ref) : arrayRemove(attendee.ref),
  });
  logActivity({
    actor,
    entity: 'check-in-date',
    entityRef: date.ref,
    entityLabel: date.date.toDate().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    action: 'check-in',
    eventRef: date.eventRef,
    meta: {
      attendeeRef: attendee.ref,
      attendeeName: attendeeLabel(attendee),
      direction: mode,
    },
  });
};

export const checkOutAttendee = async (
  actor: AuditActor | null,
  date: CheckinDate,
  attendee: Pick<Attendee, 'ref' | 'first_name' | 'last_name' | 'eventRef'>,
  mode: 'check-out' | 'uncheck-out',
): Promise<void> => {
  await updateDoc(doc(checkInDatesCollectionRef, date.ref), {
    checkedOutRefs:
      mode === 'check-out'
        ? arrayUnion(attendee.ref)
        : arrayRemove(attendee.ref),
  });
  logActivity({
    actor,
    entity: 'check-in-date',
    entityRef: date.ref,
    entityLabel: date.date.toDate().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    action: 'check-out',
    eventRef: date.eventRef,
    meta: {
      attendeeRef: attendee.ref,
      attendeeName: attendeeLabel(attendee),
      direction: mode,
    },
  });
};
